/* @jsx h */
import { defineComponent, h, ref, computed } from "vue";
import type { AgentOutput, AgentColor } from "../types";
import { AutoHeightIframe } from "./AutoHeightIframe";

// ── Color helpers ─────────────────────────────────────────────────────────

export const AGENT_COLOR_MAP: Record<
  AgentColor,
  { bg: string; text: string; border: string }
> = {
  violet: {
    bg: "bg-violet-500/15",
    text: "text-violet-400",
    border: "border-violet-500/30",
  },
  amber: {
    bg: "bg-amber-500/15",
    text: "text-amber-400",
    border: "border-amber-500/30",
  },
  sky: {
    bg: "bg-sky-500/15",
    text: "text-sky-400",
    border: "border-sky-500/30",
  },
  emerald: {
    bg: "bg-emerald-500/15",
    text: "text-emerald-400",
    border: "border-emerald-500/30",
  },
  rose: {
    bg: "bg-rose-500/15",
    text: "text-rose-400",
    border: "border-rose-500/30",
  },
  cyan: {
    bg: "bg-cyan-500/15",
    text: "text-cyan-400",
    border: "border-cyan-500/30",
  },
  orange: {
    bg: "bg-orange-500/15",
    text: "text-orange-400",
    border: "border-orange-500/30",
  },
  teal: {
    bg: "bg-teal-500/15",
    text: "text-teal-400",
    border: "border-teal-500/30",
  },
};

export const PROJECT_COLOR_MAP: Record<
  string,
  { accent: string; soft: string }
> = {
  violet: {
    accent: "text-violet-400",
    soft: "bg-violet-500/10 border-violet-500/20",
  },
  amber: {
    accent: "text-amber-400",
    soft: "bg-amber-500/10 border-amber-500/20",
  },
  sky: { accent: "text-sky-400", soft: "bg-sky-500/10 border-sky-500/20" },
  emerald: {
    accent: "text-emerald-400",
    soft: "bg-emerald-500/10 border-emerald-500/20",
  },
  rose: { accent: "text-rose-400", soft: "bg-rose-500/10 border-rose-500/20" },
  orange: {
    accent: "text-orange-400",
    soft: "bg-orange-500/10 border-orange-500/20",
  },
  teal: { accent: "text-teal-400", soft: "bg-teal-500/10 border-teal-500/20" },
  neutral: {
    accent: "text-base-content/60",
    soft: "bg-base-content/5 border-base-content/10",
  },
};

// ── MarkdownRenderer ──────────────────────────────────────────────────────

export const MarkdownRenderer = defineComponent<{
  data: { content: string };
  streaming?: boolean;
}>({
  name: "MarkdownRenderer",
  props: ["data", "streaming"],
  setup(props) {
    return () => {
      const lines = props.data.content.split("\n");
      return (
        <div class="text-sm text-base-content leading-relaxed">
          {lines.map((line, i) => {
            // Heading
            if (line.startsWith("### "))
              return (
                <h3
                  key={i}
                  class="font-semibold text-base-content mt-3 mb-1 first:mt-0"
                >
                  {line.slice(4)}
                </h3>
              );
            if (line.startsWith("## "))
              return (
                <h2
                  key={i}
                  class="font-bold text-base-content text-base mt-4 mb-1 first:mt-0"
                >
                  {line.slice(3)}
                </h2>
              );
            // Bullet
            if (line.startsWith("- "))
              return (
                <div key={i} class="flex gap-2 ml-2">
                  <span class="text-base-content/30 mt-0.5">·</span>
                  <span>{renderInline(line.slice(2))}</span>
                </div>
              );
            // Empty line
            if (line === "") return <div key={i} class="h-2" />;
            // Normal
            return (
              <p key={i} class="mb-0.5">
                {renderInline(line)}
              </p>
            );
          })}
          {props.streaming && (
            <span class="inline-block w-1.5 h-3.5 bg-accent/60 animate-pulse rounded-sm ml-0.5 align-text-bottom" />
          )}
        </div>
      );
    };
  },
});

function renderInline(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g);
  return (
    <>
      {parts.map((p, i) => {
        if (p.startsWith("**") && p.endsWith("**"))
          return (
            <strong key={i} class="font-semibold text-base-content">
              {p.slice(2, -2)}
            </strong>
          );
        if (p.startsWith("`") && p.endsWith("`"))
          return (
            <code
              key={i}
              class="text-xs font-mono bg-base-300 px-1 py-0.5 rounded text-accent"
            >
              {p.slice(1, -1)}
            </code>
          );
        const linkMatch = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (linkMatch) {
          const [, label, href] = linkMatch;
          return (
            <a
              key={i}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              class="text-accent underline hover:text-accent/80"
            >
              {label}
            </a>
          );
        }
        return <span key={i}>{p}</span>;
      })}
    </>
  );
}

// ── ChartBarRenderer ──────────────────────────────────────────────────────

export const ChartBarRenderer = defineComponent<{
  data: { labels: string[]; values: number[]; unit?: string; title?: string };
}>({
  name: "ChartBarRenderer",
  props: ["data"],
  setup(props) {
    const max = computed(() => Math.max(...props.data.values));
    return () => (
      <div class="mt-2 rounded-lg border border-base-content/10 bg-base-200/45 p-3">
        {props.data.title && (
          <p class="mb-3 text-xs font-medium text-accent">{props.data.title}</p>
        )}
        <div class="flex flex-col gap-2">
          {props.data.labels.map((label, i) => {
            const pct =
              max.value > 0 ? (props.data.values[i] / max.value) * 100 : 0;
            const val = props.data.values[i];
            const currencySymbol =
              (typeof window !== "undefined" &&
                (window as any).__LEMMON_CURRENCY__) ||
              "CHF";
            const formatted =
              props.data.unit === "CHF"
                ? `${currencySymbol} ${val.toLocaleString()}`
                : val.toLocaleString();
            return (
              <div
                key={label}
                class="grid grid-cols-[112px_minmax(0,1fr)_92px] items-center gap-2"
              >
                <span class="truncate text-right text-xs text-base-content/50">
                  {label}
                </span>
                <div class="h-4 overflow-hidden rounded bg-base-content/10">
                  <div
                    class="h-full rounded bg-accent/75 transition-all duration-500"
                    style={`width: ${pct}%`}
                  />
                </div>
                <span class="font-mono text-xs text-base-content/60">
                  {formatted}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  },
});

// ── DataTableRenderer ─────────────────────────────────────────────────────

export const DataTableRenderer = defineComponent<{
  data: {
    columns: { key: string; label: string }[];
    rows: Record<string, string | number>[];
  };
}>({
  name: "DataTableRenderer",
  props: ["data"],
  setup(props) {
    return () => (
      <div class="mt-2 overflow-hidden rounded-lg border border-base-content/10">
        <table class="w-full text-xs">
          <thead>
            <tr class="border-b border-base-content/10 bg-base-200/60">
              {props.data.columns.map((col) => (
                <th
                  key={col.key}
                  class="text-left px-3 py-2 text-base-content/50 font-medium"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {props.data.rows.map((row, i) => (
              <tr
                key={i}
                class={
                  i < props.data.rows.length - 1
                    ? "border-b border-base-content/5"
                    : ""
                }
              >
                {props.data.columns.map((col) => (
                  <td key={col.key} class="px-3 py-2 text-base-content/80">
                    {String(row[col.key] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  },
});

// ── ImageGalleryRenderer ──────────────────────────────────────────────────

export const ImageGalleryRenderer = defineComponent<{
  data: {
    images: { id: string; url: string; alt?: string; caption?: string }[];
  };
}>({
  name: "ImageGalleryRenderer",
  props: ["data"],
  setup(props) {
    const selected = ref<string | null>(null);
    return () => (
      <div class="mt-2">
        <div class="grid grid-cols-3 gap-2">
          {props.data.images.map((img) => (
            <button
              key={img.id}
              onClick={() =>
                (selected.value = selected.value === img.id ? null : img.id)
              }
              class={[
                "group relative aspect-video overflow-hidden rounded-lg border-2 transition-all duration-150",
                selected.value === img.id
                  ? "border-accent shadow-lg shadow-accent/20 scale-[1.02]"
                  : "border-transparent hover:border-base-content/20",
              ].join(" ")}
            >
              <img
                src={img.url}
                alt={img.alt}
                class="w-full h-full object-cover"
              />
              {selected.value === img.id && (
                <div class="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-accent flex items-center justify-center">
                  <span
                    class="material-symbols-outlined text-accent"
                    style="font-size:12px"
                  >
                    check
                  </span>
                </div>
              )}
              {img.caption && (
                <div class="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <p class="text-[10px] text-accent truncate">{img.caption}</p>
                </div>
              )}
            </button>
          ))}
        </div>
        {selected.value && (
          <p class="text-xs text-accent mt-2 flex items-center gap-1">
            <span class="material-symbols-outlined" style="font-size:13px">
              check_circle
            </span>
            {props.data.images.find((i) => i.id === selected.value)?.caption ??
              "Selected"}
          </p>
        )}
      </div>
    );
  },
});

// ── InstagramCardRenderer ─────────────────────────────────────────────────

export const InstagramCardRenderer = defineComponent<{
  data: {
    caption: string;
    hashtags: string[];
    imageUrl: string | null;
    scheduledFor: string | null;
  };
}>({
  name: "InstagramCardRenderer",
  props: ["data"],
  setup(props) {
    return () => (
      <div class="mt-2 max-w-xs overflow-hidden rounded-lg border border-base-content/10 bg-base-100">
        {/* Header */}
        <div class="flex items-center gap-2 px-3 py-2.5 border-b border-base-content/10">
          <div class="w-7 h-7 rounded-md bg-primary/15 border border-primary/20 flex items-center justify-center">
            <span
              class="material-symbols-outlined text-primary"
              style="font-size:14px"
            >
              restaurant
            </span>
          </div>
          <span class="text-xs font-semibold text-base-content">
            bistrolemmon
          </span>
          <span class="text-[10px] text-base-content/30 ml-auto">Preview</span>
        </div>
        {/* Image */}
        {props.data.imageUrl && (
          <div class="aspect-square overflow-hidden bg-base-300">
            <img
              src={props.data.imageUrl}
              alt="Post image"
              class="w-full h-full object-cover"
            />
          </div>
        )}
        {!props.data.imageUrl && (
          <div class="aspect-square bg-base-300 flex items-center justify-center">
            <span
              class="material-symbols-outlined text-base-content/20"
              style="font-size:40px"
            >
              image
            </span>
          </div>
        )}
        {/* Caption */}
        <div class="px-3 py-2.5">
          <p class="text-xs text-base-content leading-relaxed">
            {props.data.caption}
          </p>
          <p class="text-xs text-sky-400 mt-1">
            {props.data.hashtags.join(" ")}
          </p>
        </div>
      </div>
    );
  },
});

// ── SvgImageRenderer ──────────────────────────────────────────────────────

const PLATFORM_LABELS: Record<string, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  twitter: "Twitter / X",
};

export const SvgImageRenderer = defineComponent<{
  data: {
    svg: string;
    title?: string;
    platform?: "instagram" | "facebook" | "twitter";
    format?: "post" | "story";
  };
}>({
  name: "SvgImageRenderer",
  props: ["data"],
  setup(props) {
    const aspectClass = computed(() =>
      props.data.format === "story"
        ? "aspect-[9/16] max-w-[260px]"
        : "aspect-square max-w-sm",
    );
    return () => (
      <div class="mt-2">
        {(props.data.title || props.data.platform) && (
          <div class="mb-1.5 flex items-center gap-2 text-xs text-base-content/45">
            {props.data.title && (
              <span class="font-medium text-base-content/70">
                {props.data.title}
              </span>
            )}
            {props.data.platform && (
              <span class="rounded-md bg-base-content/5 px-1.5 py-0.5 text-[10px]">
                {PLATFORM_LABELS[props.data.platform] ?? props.data.platform}
                {props.data.format ? ` · ${props.data.format}` : ""}
              </span>
            )}
          </div>
        )}
        <div
          class={`overflow-hidden rounded-lg border border-base-content/10 bg-base-200/45 ${aspectClass.value}`}
          innerHTML={props.data.svg}
        />
      </div>
    );
  },
});

// ── WebsitePreviewRenderer ────────────────────────────────────────────────

export const WebsitePreviewRenderer = defineComponent<{
  data: { url: string; branch: string };
}>({
  name: "WebsitePreviewRenderer",
  props: ["data"],
  setup(props) {
    return () => (
      <div class="mt-2 max-w-sm overflow-hidden rounded-lg border border-base-content/10">
        {/* Browser chrome */}
        <div class="flex items-center gap-1.5 px-3 py-2 bg-base-300 border-b border-base-content/10">
          <span class="w-2.5 h-2.5 rounded-full bg-error/50" />
          <span class="w-2.5 h-2.5 rounded-full bg-warning/50" />
          <span class="w-2.5 h-2.5 rounded-full bg-success/50" />
          <div class="flex-1 mx-2 bg-base-content/10 rounded px-2 py-0.5 text-[10px] font-mono text-base-content/40 truncate">
            {props.data.url}
          </div>
          <a
            href={props.data.url}
            target="_blank"
            rel="noopener noreferrer"
            class="text-base-content/30 hover:text-base-content/60 transition-colors"
          >
            <span class="material-symbols-outlined" style="font-size:13px">
              open_in_new
            </span>
          </a>
        </div>
        {/* Preview area */}
        <div class="aspect-video bg-base-200 flex flex-col items-center justify-center gap-2 relative overflow-hidden">
          <div
            class="absolute inset-0 opacity-[0.03]"
            style="background-image: linear-gradient(45deg, #888 25%, transparent 25%), linear-gradient(-45deg, #888 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #888 75%), linear-gradient(-45deg, transparent 75%, #888 75%); background-size: 16px 16px;"
          />
          <span
            class="material-symbols-outlined text-base-content/20"
            style="font-size:32px"
          >
            web
          </span>
          <span class="text-[11px] text-base-content/30 font-mono">
            {props.data.branch}
          </span>
        </div>
      </div>
    );
  },
});

// ── WebsiteDiffRenderer ───────────────────────────────────────────────────

export const WebsiteDiffRenderer = defineComponent<{
  data: { file: string; before: string; after: string };
}>({
  name: "WebsiteDiffRenderer",
  props: ["data"],
  setup(props) {
    const open = ref(false);
    return () => (
      <div class="mt-2">
        <button
          onClick={() => (open.value = !open.value)}
          class="flex items-center gap-2 text-xs text-base-content/50 hover:text-base-content/70 transition-colors"
        >
          <span class="material-symbols-outlined" style="font-size:13px">
            difference
          </span>
          <span class="font-mono">{props.data.file}</span>
          <span
            class="material-symbols-outlined transition-transform duration-150"
            style={`font-size:12px; transform:rotate(${open.value ? 180 : 0}deg)`}
          >
            expand_more
          </span>
        </button>
        {open.value && (
          <div class="mt-2 overflow-hidden rounded-lg border border-base-content/10 font-mono text-[11px]">
            {props.data.before.split("\n").map((line, i) => (
              <div
                key={`b${i}`}
                class="flex bg-error/10 px-3 py-0.5 text-error/80"
              >
                <span class="mr-2 select-none">−</span>
                {line}
              </div>
            ))}
            {props.data.after.split("\n").map((line, i) => (
              <div
                key={`a${i}`}
                class="flex bg-success/10 px-3 py-0.5 text-success/80"
              >
                <span class="mr-2 select-none">+</span>
                {line}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  },
});

// ── ApprovalActionsRenderer ───────────────────────────────────────────────

export const ApprovalActionsRenderer = defineComponent<{
  data: {
    question: string;
    actions: { id: string; label: string; style: string }[];
  };
}>({
  name: "ApprovalActionsRenderer",
  props: ["data"],
  setup(props) {
    const done = ref<string | null>(null);
    const btnClass = (style: string, chosen: boolean) => {
      const base = "btn btn-sm gap-1 transition-all";
      if (chosen) return `${base} btn-primary`;
      if (style === "primary") return `${base} btn-primary`;
      if (style === "error") return `${base} btn-error btn-outline`;
      return `${base} btn-ghost`;
    };
    return () => (
      <div class="mt-2 flex flex-wrap items-center gap-2 border-t border-base-content/10 pt-2">
        <span class="text-xs text-accent">{props.data.question}</span>
        {props.data.actions.map((action) => (
          <button
            key={action.id}
            disabled={done.value !== null}
            onClick={() => (done.value = action.id)}
            class={btnClass(action.style, done.value === action.id)}
          >
            {done.value === action.id && (
              <span class="material-symbols-outlined" style="font-size:13px">
                check
              </span>
            )}
            {action.label}
          </button>
        ))}
      </div>
    );
  },
});

// ── ArtifactRenderer ──────────────────────────────────────────────────────

export const ArtifactRenderer = defineComponent<{
  data: {
    artifactId: string;
    name: string;
    type: string;
    status: string;
    previewUrl: string | null;
    deployedUrl: string | null;
  };
}>({
  name: "ArtifactRenderer",
  props: ["data"],
  setup(props) {
    const statusColor =
      props.data.status === "deployed" ? "text-success" : "text-warning";
    const frameUrl = props.data.deployedUrl ?? props.data.previewUrl ?? null;
    return () => (
      <div class="mt-2">
        <div class="flex items-center gap-3 rounded-lg border border-base-content/10 bg-base-200/45 px-3 py-2.5">
          <span
            class="material-symbols-outlined text-accent"
            style="font-size:20px"
          >
            deployed_code
          </span>
          <div class="flex-1 min-w-0">
            <p class="text-xs font-medium text-base-content truncate">
              {props.data.name}
            </p>
            <p class={`text-[10px] capitalize ${statusColor}`}>
              {props.data.status}
            </p>
          </div>
          {frameUrl && (
            <a
              href={frameUrl}
              target="_blank"
              rel="noopener noreferrer"
              class="btn btn-xs btn-ghost gap-1"
            >
              <span class="material-symbols-outlined" style="font-size:13px">
                open_in_new
              </span>
              Open
            </a>
          )}
        </div>
        {frameUrl && <AutoHeightIframe src={frameUrl} />}
      </div>
    );
  },
});

// ── MessageBlock dispatcher ───────────────────────────────────────────────

export const MessageBlock = defineComponent<{
  output: AgentOutput;
  streaming?: boolean;
}>({
  name: "MessageBlock",
  props: ["output", "streaming"],
  setup(props) {
    return () => {
      const o = props.output;
      switch (o.renderer) {
        case "markdown":
          return (
            <MarkdownRenderer
              data={o.data as any}
              streaming={props.streaming}
            />
          );
        case "chart_bar":
          return <ChartBarRenderer data={o.data as any} />;
        case "data_table":
          return <DataTableRenderer data={o.data as any} />;
        case "image_gallery":
          return <ImageGalleryRenderer data={o.data as any} />;
        case "instagram_card":
          return <InstagramCardRenderer data={o.data as any} />;
        case "website_preview":
          return <WebsitePreviewRenderer data={o.data as any} />;
        case "website_diff":
          return <WebsiteDiffRenderer data={o.data as any} />;
        case "approval_actions":
          return <ApprovalActionsRenderer data={o.data as any} />;
        case "artifact":
          return <ArtifactRenderer data={o.data as any} />;
        case "svg_image":
          return <SvgImageRenderer data={o.data as any} />;
        default:
          return (
            <div class="text-xs text-base-content/30 font-mono">
              [{o.renderer}]
            </div>
          );
      }
    };
  },
});
